import { useState } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Download } from "lucide-react";

export function CsvGenerator() {
  const [count, setCount] = useState(1000);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateData = () => {
    setIsGenerating(true);
        // Simulate generation to avoid blocking the main thread
    setTimeout(() => {
      const header = "ID,FirstName,LastName,Email,Phone,Department,JobTitle,Country,Age,Salary,JoiningDate,Status,IsRemote\n";
      const firstNames = ["John", "Jane", "Alice", "Bob", "Charlie", "David", "Emma", "Frank", "Grace", "Henry"];
      const lastNames = ["Smith", "Doe", "Johnson", "Brown", "Williams", "Miller", "Davis", "Wilson", "Anderson", "Taylor"];
      const depts = ["Engineering", "Marketing", "Sales", "HR", "Legal", "Operations", "Finance"];
      const jobTitles = ["Developer", "Manager", "Analyst", "Designer", "Director", "Specialist"];
      const countries = ["USA", "Canada", "UK", "Germany", "France", "Japan", "Australia"];
      
      let csvContent = header;
      
      for (let i = 1; i <= count; i++) {
        const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
        const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
        const email = `user${i}@example.com`;
        const phone = `555-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`;
        const dept = depts[Math.floor(Math.random() * depts.length)];
        const jobTitle = jobTitles[Math.floor(Math.random() * jobTitles.length)];
        const country = countries[Math.floor(Math.random() * countries.length)];
        const age = Math.floor(Math.random() * 40) + 20;
        const salary = Math.floor(Math.random() * 80000) + 40000;
        const joiningDate = `202${Math.floor(Math.random() * 5)}-0${Math.floor(Math.random() * 9) + 1}-15`;
        const status = Math.random() > 0.5 ? "Active" : "Inactive";
        const isRemote = Math.random() > 0.3 ? "Yes" : "No";
        
        csvContent += `${i},${firstName},${lastName},${email},${phone},${dept},${jobTitle},${country},${age},${salary},${joiningDate},${status},${isRemote}\n`;
      }
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `generated_data_${count}_rows.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsGenerating(false);
    }, 100);
  };

  return (
    <ToolPageLayout
      title="Free CSV Data Generator – Generate Massive Mock Datasets Online"
      description="Need test data? Instantly generate large-scale mock CSV files for database seeding, API testing, and software development. Create custom datasets with up to 1,000,000 rows of fake names, emails, salaries, and more. Fast, secure, and runs entirely in your browser with no data upload."
      h1="Online Massive CSV Data Generator"
      category="Data Tools"
      categoryPath="/data-tools"
      howToUse={[
        "Select the number of rows you need (from 1 to 1,000,000).",
        "Click 'Download Generated CSV' to instantly generate the file.",
        "Import your mock dataset directly into SQL databases, Excel, or testing environments."
      ]}
      faq={[
        { question: "Why use an online CSV data generator?", answer: "Generating large datasets of fake user info is crucial for testing database performance, application load balancing, and UI data-binding without needing real personal data." },
        { question: "Is it secure to generate CSV data here?", answer: "Yes. Our CSV generator works 100% locally in your browser. None of your data is sent to our servers; it is processed in your RAM, ensuring maximum privacy." },
        { question: "What file formats does this generator produce?", answer: "We produce standard, clean CSV files compatible with Microsoft Excel, Google Sheets, PostgreSQL, MySQL, and most NoSQL database importers." },
        { question: "Does this tool support SQL database seeding?", answer: "Absolutely. The generated CSV files can be directly mapped to your SQL table schemas (ID, Email, Department, Job Title, etc.) for rapid database seeding and stress testing." }
      ]}
    >
      <div className="w-full bg-white border border-slate-200 p-4 sm:p-6 rounded-2xl shadow-xs space-y-6">
        <div className="text-sm text-slate-600 mb-4">
          <p className="mb-2"><strong>Mock Dataset Fields Include:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Full User Profiles (ID, Name, Email, Phone)</li>
            <li>Professional Data (Department, Job Title, Salary)</li>
            <li>Demographic Info (Country, Age)</li>
            <li>Status Flags (Active/Inactive, Remote/Office)</li>
          </ul>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-700">Number of Records (Max 1,000,000)</Label>
          <Input 
            type="number" 
            value={count} 
            onChange={(e) => setCount(Math.min(1000000, Math.max(1, Number(e.target.value))))} 
            className="h-12 text-lg font-mono"
          />
        </div>
        
        <Button 
          onClick={generateData} 
          disabled={isGenerating}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 font-bold text-lg gap-2"
        >
          {isGenerating ? "Generating..." : <><Download className="w-5 h-5" /> Download {count.toLocaleString()} Records</>}
        </Button>
        <p className="text-xs text-slate-500 text-center">Data is generated locally in your browser RAM.</p>
      </div>
    </ToolPageLayout>
  );
}
